import { v4 as uuid } from 'uuid';
import fetch from 'isomorphic-fetch';
import createAction from './createAction';
import normalizeSponsoredProducts from './sponsoredProducts.normalizer';
import * as UISettings from '../../utils/UISettings';

// because icepick is implemented using commonjs modules,
// which we don't suppport, it has to be required in this way
const icepick = require('../../../node_modules/icepick/icepick.min');

// 1. Initial state
const initialState = icepick.freeze({
  storedResponses: [],
  list: [],
  sponsoredProducts: [],
  sponsoredProductsLoading: false,
  flexibleCreditTooltipProductID: null,
  briefDescriptionProductID: null,
  emailWhenBackFormProductID: null,
  emailWhenBackXhrResponseContent: null
});

// 2. Actions
const STORE_LISTING_XHR_RESPONSE = 'listingPage/products/STORE_LISTING_XHR_RESPONSE';
const REMOVE_STORED_RESPONSE = 'listingPage/products/REMOVE_STORED_RESPONSE';
const FETCH_SPONSORED_PRODUCTS = 'listingPage/products/FETCH_SPONSORED_PRODUCTS';
const FETCH_SPONSORED_PRODUCTS_FULFILLED =
  'listingPage/products/FETCH_SPONSORED_PRODUCTS_FULFILLED';
const FETCH_SPONSORED_PRODUCTS_REJECTED = 'listingPage/products/FETCH_SPONSORED_PRODUCTS_REJECTED';
const SET_PRODUCTS_PLACE_AVAILABILITIES = 'listingPage/products/SET_PRODUCTS_PLACE_AVAILABILITIES';
const REMOVE_ALL_PLACE_SPECIFIC_AVAILABILITIES =
  'listingPage/products/REMOVE_ALL_PLACE_SPECIFIC_AVAILABILITIES';
const SET_FLEXIBLE_CREDIT_TOOLTIP_PRODUCT_ID =
  'listingPage/products/SET_FLEXIBLE_CREDIT_TOOLTIP_PRODUCT_ID';
const SET_BRIEF_DESCRIPTION_PRODUCT_ID = 'listingPage/products/SET_BRIEF_DESCRIPTION_PRODUCT_ID';
const SET_EMAIL_ME_BACK_PRODUCT_ID = 'listingPage/products/SET_EMAIL_ME_BACK_PRODUCT_ID';
const SET_EMAIL_ME_BACK_XHR_RESPONSE = 'listingPage/products/SET_EMAIL_ME_BACK_XHR_RESPONSE';
const SET_ACTIVE_STORED_RESPONSE = 'listingPage/products/SET_ACTIVE_STORED_RESPONSE';

// 3. Action creators
export function getSponsoredProducts(segmentId) {
  return (dispatch) => {
    dispatch(fetchSponsoredProducts());

    const storeApiUrl = UISettings.get('component.hooklogic.api');
    const queryParams = `?ucms=${segmentId}`;
    const hooklogicApiUrl = `${storeApiUrl}/hooklogicProducts/${queryParams}`;
    const timeout = Number(UISettings.get('component.hooklogic.timeout'));
    let shouldContinue = true;

    setTimeout(() => {
      shouldContinue = false;
    }, timeout);

    fetch(hooklogicApiUrl, {
      method: 'GET',
      credentials: 'include'
    })
      .then((res) => {
        if ((!res || !res.ok) && !shouldContinue) {
          throw new Error('[GET] respose not ok.');
        }

        return res.json();
      })
      .then((data) => dispatch(
        fetchSponsoredProductsFulfilled(
          normalizeSponsoredProducts(data.payload)
        )
      ))
      .catch((err) => {
        dispatch(fetchSponsoredProductsRejected());
        console.error(`[GET] fetch sponsoredProducts failed, got ${err}`);
      });
  };
}

export function fetchSponsoredProducts(payload = null) {
  return createAction(FETCH_SPONSORED_PRODUCTS)(payload);
}

export function fetchSponsoredProductsFulfilled(products) {
  return createAction(FETCH_SPONSORED_PRODUCTS_FULFILLED)(products);
}

export function fetchSponsoredProductsRejected(payload = null) {
  return createAction(FETCH_SPONSORED_PRODUCTS_REJECTED)(payload);
}

export function storeListingXhrResponse(payload) {
  return createAction(STORE_LISTING_XHR_RESPONSE)(payload);
}

export function removeStoredResponse(responseUuid) {
  return createAction(REMOVE_STORED_RESPONSE)(responseUuid);
}

export function setActiveStoredResponse(responseUuid) {
  return createAction(SET_ACTIVE_STORED_RESPONSE)(responseUuid);
}

export function setProductsPlaceAvailabilities(payload) {
  return createAction(SET_PRODUCTS_PLACE_AVAILABILITIES)(payload);
}

export function removeAllPlaceSpecificAvailabilities() {
  return createAction(REMOVE_ALL_PLACE_SPECIFIC_AVAILABILITIES)();
}

export function showFlexibleCreditTooltip(productId) {
  return createAction(SET_FLEXIBLE_CREDIT_TOOLTIP_PRODUCT_ID)(productId);
}

export function hideFlexibleCreditTooltip() {
  // This function is mainly called from componentWillUnmount in product component to avoid
  // multiple dispatches into redux store from every unmounted product component
  return (dispatch, getState) => {
    if (getState().listingPage.products.flexibleCreditTooltipProductID !== null) {
      dispatch(createAction(SET_FLEXIBLE_CREDIT_TOOLTIP_PRODUCT_ID)(null));
    }
  };
}

export function showBriefProductDescription(productId) {
  return createAction(SET_BRIEF_DESCRIPTION_PRODUCT_ID)(productId);
}

export function hideBriefProductDescription() {
  // This function is mainly called from componentWillUnmount in product component to avoid
  // multiple dispatches into redux story from every unmounted product component
  return (dispatch, getState) => {
    if (getState().listingPage.products.briefDescriptionProductID !== null) {
      dispatch(createAction(SET_BRIEF_DESCRIPTION_PRODUCT_ID)(null));
    }
  };
}

export function setEmailMeBackProductID(productId) {
  return createAction(SET_EMAIL_ME_BACK_PRODUCT_ID)(productId);
}

export function setEmailMeBackXhrResponse(response) {
  return createAction(SET_EMAIL_ME_BACK_XHR_RESPONSE)(response);
}

// 4. Reducer
export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case FETCH_SPONSORED_PRODUCTS: {
      return icepick.updateIn(state, ['sponsoredProductsLoading'], () => true);
    }
    case FETCH_SPONSORED_PRODUCTS_FULFILLED: {
      const newState = icepick.updateIn(state, ['sponsoredProducts'], () => action.payload);
      return icepick.updateIn(newState, ['sponsoredProductsLoading'], () => false);
    }
    case FETCH_SPONSORED_PRODUCTS_REJECTED: {
      const newState = icepick.updateIn(state, ['sponsoredProducts'], () => []);
      return icepick.updateIn(newState, ['sponsoredProductsLoading'], () => false);
    }
    case STORE_LISTING_XHR_RESPONSE: {
      // set isActive to false in all existing storedResponses
      const newState = icepick.updateIn(state, ['storedResponses'], (storedResponses) => icepick.map((response) => (
        icepick.setIn(response, ['isActive'], false)
      ), storedResponses));

      const request = action.payload.request;
      delete action.payload.request;

      // set isActive to true in the newest storedResponse
      return icepick.updateIn(newState, ['storedResponses'], (storedResponses) => (
        icepick.push(storedResponses,
          {
            timeFetched: Date.now(),
            uuid: uuid(),
            isActive: true,
            request: request,
            data: action.payload
          })
      ));
    }
    case REMOVE_STORED_RESPONSE: {
      // eslint-disable-next-line max-len
      return icepick.updateIn(state, ['storedResponses'], (storedResponses) => icepick.filter((response) => response.uuid !== action.payload, storedResponses));
    }
    case SET_ACTIVE_STORED_RESPONSE: {
      return icepick.setIn(state, ['storedResponses'],
        icepick.map((response) => {
          if (response.uuid === action.payload) {
            return { ...response, isActive: true };
          }
          return { ...response, isActive: false };
        }, state.storedResponses));
    }
    case SET_PRODUCTS_PLACE_AVAILABILITIES: {
      let mutableStoredResponses = JSON.parse(JSON.stringify(state.storedResponses));

      action.payload.availabilitiesList.forEach((productAvailability) => {
        mutableStoredResponses = mutableStoredResponses.map((storedResponse) => {
          storedResponse.data.productList = storedResponse.data.productList.map((product) => {
            if (product.id === productAvailability.getProductId()) {
              storedResponse.data.placeSpecificAvailabilityPostcode =
                action.payload.locationPostcode;
              product.channelsAvailability.placeSpecificAvailability = {};

              if (!productAvailability.isSuccessful()) {
                return product; // return with empty placeSpecificAvailability
              }

              product.channelsAvailability.placeSpecificAvailability.delivery = {
                label: productAvailability.getDeliveryLabel(),
                type: productAvailability.isDeliverable()
                  ? 'homeDeliveryAvailable' : 'homeDeliveryUnavailable'
              };

              // If delivery and collection has the same label, store only delivery instead.
              // Ignore collection data.
              if (productAvailability.getCollectionLabel() !==
                productAvailability.getDeliveryLabel()) {
                product.channelsAvailability.placeSpecificAvailability.inStore = {
                  label: productAvailability.getCollectionLabel(),
                  type: productAvailability.isCollectable()
                    ? 'collectInStoreAvailable' : 'collectInStoreUnavailable'
                };
              }
            }
            return product;
          });
          return storedResponse;
        });
      });

      return icepick.merge(state, { storedResponses: mutableStoredResponses });
    }
    case REMOVE_ALL_PLACE_SPECIFIC_AVAILABILITIES: {
      return icepick.setIn(state, ['storedResponses'], state.storedResponses.map((response) => ({
        ...response,
        data: {
          ...response.data,
          placeSpecificAvailabilityPostcode: null,
          productList: response.data.productList.map((product) => (
            icepick.unsetIn(product, ['channelsAvailability', 'placeSpecificAvailability'])
          ))
        }
      })));
    }
    case SET_FLEXIBLE_CREDIT_TOOLTIP_PRODUCT_ID: {
      return icepick.setIn(state, ['flexibleCreditTooltipProductID'], action.payload);
    }
    case SET_BRIEF_DESCRIPTION_PRODUCT_ID: {
      return icepick.setIn(state, ['briefDescriptionProductID'], action.payload);
    }
    case SET_EMAIL_ME_BACK_PRODUCT_ID: {
      return icepick.setIn(state, ['emailWhenBackFormProductID'], action.payload);
    }
    case SET_EMAIL_ME_BACK_XHR_RESPONSE: {
      return icepick.setIn(state, ['emailWhenBackXhrResponseContent'], action.payload);
    }
    default: {
      return state;
    }
  }
}
